import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import { reactivateTenantCallable } from '@/lib/callables';
import type { TenantId } from '@etiquetachef/types';

type Props = {
  open: boolean;
  tenantId: TenantId;
  tenantName: string;
  onClose: () => void;
};

/**
 * Reativa um tenant suspenso (`reactivateTenant`), com motivo obrigatório.
 *
 * Origem: sem issue — commit efa5a8ca2, 2026-04-26 17:22 +0200 (Sprint 2).
 *
 * Contrato: motivo vazio → erro local sem chamar; senão
 * `reactivateTenantCallable({ tenantId, reason })`, fecha no sucesso,
 * `err.message` em `Alert` dentro do modal no erro (#1021). O servidor grava `tenants/{id}.status =
 * ACTIVE`, `billing/subscription.status = ACTIVE` e audit; idempotente se já
 * ativo. Não checa se a causa da suspensão foi resolvida — é de quem clica
 * (`apps/functions/src/admin/reactivateTenant.ts`). Permissão no servidor:
 * `requireTenantOperator` com `staffRoles: ADMIN_PERMISSIONS.reactivateTenant`
 * (ROOT/OPS — `docs/permissions.md`, tabela de tenant) ou ADMIN do CRM
 * (`crmAdminOnly`); FINANCE/SUPPORT recebem `permission-denied` no `Alert`.
 *
 * Usado por: `BillingActions`, montado em `routes/TenantDetailPage.tsx`
 * (rota `/tenants/:id`, aberta pela lista do menu "Tenants"), admin web.
 * Spec: `docs/screens/tenant-detail-admin.md`.
 *
 * Histórico:
 *  - 2026-06-08 21:47 +0200 — Cloudscape (39-E).
 *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function ReactivateDialog({ open, tenantId, tenantName, onClose }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Limpa motivo e erro e devolve o controle ao pai. */
  function close(): void {
    setReason('');
    setError(null);
    onClose();
  }

  /** Valida o motivo e chama `reactivateTenant`; erro vira `Alert` no modal (#1021). */
  async function handleSubmit(): Promise<void> {
    if (reason.trim().length === 0) {
      setError(t('tenantDetail.actions.reasonRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await reactivateTenantCallable({ tenantId, reason: reason.trim() });
      close();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) close();
      }}
      header={t('tenantDetail.actions.reactivateTitle')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {t('tenantDetail.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void handleSubmit();
              }}
              loading={submitting}
            >
              {t('tenantDetail.actions.confirmReactivate')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          {t('tenantDetail.actions.reactivateDescription', { name: tenantName })}
        </Box>
        <FormField label={t('tenantDetail.actions.reasonLabel')}>
          <Textarea
            value={reason}
            onChange={({ detail }): void => {
              setReason(detail.value);
              // #1021 (2026-10-25 15:00 +0100): digitar o motivo tira o "motivo obrigatório" do Alert.
              setError(null);
            }}
            rows={3}
            disabled={submitting}
            placeholder={t('tenantDetail.actions.reasonPlaceholder')}
          />
        </FormField>
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
            do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
